import React, { useState } from 'react';
import { MainLayout } from '../layouts/MainLayout';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Tabs } from '../components/ui/Tabs';
import { TerritoryMap } from '../components/crm/TerritoryMap';
import { VoiceAgentModal } from '../components/crm/VoiceAgentModal';
import { CallHistoryPanel } from '../components/crm/CallHistoryPanel';
import { Client, PipelineStage, VoiceAgentCall } from '../types';
import { Phone, Mail, MapPin, ExternalLink, MoreHorizontal, List, Map, PhoneCall } from 'lucide-react';
const pipeline: PipelineStage[] = [{
  id: '1',
  name: 'Erstkontakt',
  value: 12000,
  count: 5,
  color: 'bg-slate-200'
}, {
  id: '2',
  name: 'Qualifizierung',
  value: 45000,
  count: 3,
  color: 'bg-blue-200'
}, {
  id: '3',
  name: 'Angebot',
  value: 28000,
  count: 2,
  color: 'bg-amber-200'
}, {
  id: '4',
  name: 'Verhandlung',
  value: 15000,
  count: 1,
  color: 'bg-purple-200'
}, {
  id: '5',
  name: 'Abschluss',
  value: 8500,
  count: 4,
  color: 'bg-emerald-200'
}];
const mockClients: Client[] = [{
  id: '1',
  name: 'Müller GmbH',
  contactPerson: 'Hans Müller',
  email: 'h.mueller@mueller-gmbh.de',
  phone: '+49 30 123456',
  status: 'active'
}, {
  id: '2',
  name: 'Schmidt KG',
  contactPerson: 'Petra Schmidt',
  email: 'p.schmidt@schmidt-kg.de',
  phone: '+49 40 987654',
  status: 'active'
}, {
  id: '3',
  name: 'Praxis Dr. Stein',
  contactPerson: 'Dr. Med. Stein',
  email: 'info@praxis-stein.de',
  phone: '+49 89 555666',
  status: 'active'
}, {
  id: '4',
  name: 'StartUp Rocket',
  contactPerson: 'Tim Tech',
  email: 'tim@rocket.io',
  phone: '+49 170 112233',
  status: 'inactive'
}];
const mockCallHistory: VoiceAgentCall[] = [{
  id: 'c1',
  clientId: '1',
  clientName: 'Müller GmbH',
  clientPhone: '+49 30 123456',
  promptId: 'p2',
  promptName: 'Zufriedenheitsumfrage',
  status: 'completed',
  transcript: 'Agent: Guten Tag Herr Müller! Wir möchten kurz Ihre Zufriedenheit mit unseren Services erfragen.\n\nKunde: Ja gerne, ich habe gerade Zeit.\n\nAgent: Wie zufrieden sind Sie auf einer Skala von 1-10?\n\nKunde: Ich würde sagen eine 8. Die Reaktionszeit könnte manchmal schneller sein.\n\nAgent: Vielen Dank für das Feedback. Gibt es noch weitere Verbesserungswünsche?\n\nKunde: Nein, ansonsten sind wir sehr zufrieden.\n\nAgent: Vielen Dank für Ihre Zeit!',
  notes: 'Kunde sehr zufrieden, Reaktionszeit könnte verbessert werden',
  duration: 180,
  createdAt: '24.10.2023 14:30',
  completedAt: '24.10.2023 14:33'
}, {
  id: 'c2',
  clientId: '2',
  clientName: 'Schmidt KG',
  clientPhone: '+49 40 987654',
  promptId: 'p1',
  promptName: 'Terminvereinbarung',
  status: 'in_progress',
  createdAt: '25.10.2023 10:15'
}, {
  id: 'c3',
  clientId: '3',
  clientName: 'Praxis Dr. Stein',
  clientPhone: '+49 89 555666',
  promptId: 'p5',
  promptName: 'Update-Erinnerung',
  status: 'completed',
  transcript: 'Agent: Guten Tag! Wir haben festgestellt, dass bei Ihnen noch wichtige System-Updates ausstehen.\n\nKunde: Oh, das wusste ich nicht. Wie dringend ist das?\n\nAgent: Es handelt sich um Sicherheitsupdates, die wir zeitnah durchführen sollten.\n\nKunde: Können wir das nächste Woche Dienstag machen?\n\nAgent: Perfekt, ich trage Sie für Dienstag, 10 Uhr ein.\n\nKunde: Danke!',
  notes: 'Termin vereinbart für Dienstag, 31.10. um 10:00 Uhr',
  duration: 120,
  createdAt: '23.10.2023 16:45',
  completedAt: '23.10.2023 16:47'
}];
interface CRMProps {
  onLogout: () => void;
}
export function CRM({
  onLogout
}: CRMProps) {
  const [activeTab, setActiveTab] = useState('list');
  const [isVoiceAgentModalOpen, setIsVoiceAgentModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [callHistory, setCallHistory] = useState<VoiceAgentCall[]>(mockCallHistory);
  const handleStartCall = (clientId: string, promptId: string, customPrompt?: string) => {
    const client = mockClients.find(c => c.id === clientId);
    if (!client) return;
    const newCall: VoiceAgentCall = {
      id: `c${Date.now()}`,
      clientId,
      clientName: client.name,
      clientPhone: client.phone,
      promptId,
      promptName: promptId === 'custom' ? 'Benutzerdefiniert' : 'Prompt',
      status: 'pending',
      createdAt: new Date().toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    };
    setCallHistory([newCall, ...callHistory]);
  };
  const handleOpenVoiceAgent = (client: Client) => {
    setSelectedClient(client);
    setIsVoiceAgentModalOpen(true);
  };
  return <MainLayout title="CRM System" onLogout={onLogout} data-id="element-307">
      {/* Sales Pipeline */}
      <div className="mb-8" data-id="element-308">
        <h2 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-309">
          Sales Pipeline
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-310">
          {pipeline.map(stage => <Card key={stage.id} className="text-center border-t-4" style={{
          borderColor: stage.color.replace('bg-', 'text-').replace('200', '500')
        }} data-id="element-311">
              <h3 className="text-sm font-medium text-slate-500 mb-1" data-id="element-312">
                {stage.name}
              </h3>
              <div className="text-xl font-bold text-slate-900" data-id="element-313">
                {stage.count}
              </div>
              <div className="text-xs text-slate-400 mt-1" data-id="element-314">
                € {stage.value.toLocaleString()}
              </div>
            </Card>)}
        </div>
      </div>

      {/* Customer View with Tabs */}
      <Card title="Kundenübersicht" action={<Button size="sm" data-id="element-316">Neuer Kunde</Button>} noPadding data-id="element-315">
        <div className="px-6 pt-2" data-id="element-317">
          <Tabs tabs={[{
          id: 'list',
          label: 'Listenansicht'
        }, {
          id: 'map',
          label: 'Kartenansicht'
        }, {
          id: 'calls',
          label: 'Anrufverlauf'
        }]} activeTab={activeTab} onChange={setActiveTab} data-id="element-318" />
        </div>

        <div className="p-6" data-id="element-319">
          {activeTab === 'list' && <div className="overflow-x-auto" data-id="element-320">
              <table className="w-full text-sm text-left" data-id="element-321">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-322">
                  <tr data-id="element-323">
                    <th className="px-4 py-3 font-medium" data-id="element-324">Firma</th>
                    <th className="px-4 py-3 font-medium" data-id="element-325">Ansprechpartner</th>
                    <th className="px-4 py-3 font-medium" data-id="element-326">Kontakt</th>
                    <th className="px-4 py-3 font-medium" data-id="element-327">Status</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-328">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-329">
                  {mockClients.map(client => <tr key={client.id} className="hover:bg-slate-50 transition-colors" data-id="element-330">
                      <td className="px-4 py-3 font-medium text-slate-900" data-id="element-331">
                        {client.name}
                      </td>
                      <td className="px-4 py-3 text-slate-600" data-id="element-332">
                        {client.contactPerson}
                      </td>
                      <td className="px-4 py-3" data-id="element-333">
                        <div className="flex flex-col gap-1 text-xs" data-id="element-334">
                          <div className="flex items-center gap-2 text-slate-600" data-id="element-335">
                            <Mail className="h-3 w-3" data-id="element-336" /> {client.email}
                          </div>
                          <div className="flex items-center gap-2 text-slate-600" data-id="element-337">
                            <Phone className="h-3 w-3" data-id="element-338" /> {client.phone}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-339">
                        <Badge variant={client.status === 'active' ? 'success' : 'secondary'} data-id="element-340">
                          {client.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-341">
                        <div className="flex items-center justify-end gap-2" data-id="element-342">
                          <Button variant="outline" size="sm" onClick={() => handleOpenVoiceAgent(client)} leftIcon={<PhoneCall className="h-4 w-4" data-id="element-344" />} className="text-teal-600 border-teal-300 hover:bg-teal-50" data-id="element-343">
                            AI Anruf
                          </Button>
                          <Button variant="ghost" size="icon" title="Details" data-id="element-345">
                            <ExternalLink className="h-4 w-4" data-id="element-346" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Mehr" data-id="element-347">
                            <MoreHorizontal className="h-4 w-4" data-id="element-348" />
                          </Button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}

          {activeTab === 'map' && <TerritoryMap data-id="element-349" />}

          {activeTab === 'calls' && <CallHistoryPanel calls={callHistory} data-id="element-350" />}
        </div>
      </Card>

      {/* Voice Agent Modal */}
      <VoiceAgentModal isOpen={isVoiceAgentModalOpen} onClose={() => setIsVoiceAgentModalOpen(false)} client={selectedClient} onStartCall={handleStartCall} data-id="element-351" />
    </MainLayout>;
}